/**
 * The site's image lightbox: a native <dialog> (Views/Shared/ImageLightbox.cshtml, rendered by
 * the layout, mounted by siteDialogs.ts) that shows a picture large, with its caption, the pictures around it, and a
 * download when the page offers one. It opens from two kinds of trigger:
 *
 * - `a[data-lightbox]` links, which carry the picture and its caption in `data-lightbox-*`
 *   attributes and link to a page or file that works without JavaScript;
 * - `img[data-zoom]` images, including ones editors mark up in Contentful posts, whose large
 *   picture is `data-zoom-src` and whose caption comes from the surrounding figure.
 *
 * The pictures stepped through are the triggers in the same `[data-lightbox-group]`, or else
 * in the same article or section, leaving out a carousel's cloned slides and hidden copies.
 */

import { closeOnDismiss, isOutsidePicture, isPlainClick, trackModal } from "../shared/dialog";
import { captionFor, largeImageUrl, surfaceFor } from "./image-sources";

const TRIGGERS = "a[data-lightbox], img[data-zoom]";

interface Picture {
    trigger: HTMLElement;
    src: string;
    title: string;
    description: string;
    details: string;
    download?: string;
    surface?: string;
}

function pictureFor(trigger: HTMLElement): Picture {
    if (trigger instanceof HTMLImageElement) {
        const source =
            trigger.dataset.zoomSrc ||
            trigger.currentSrc ||
            trigger.src ||
            trigger.dataset.src ||
            "";
        const { title, credit } = captionFor(trigger);
        return {
            trigger,
            src: largeImageUrl(source),
            title,
            description: "",
            details: credit,
            surface: surfaceFor(source),
        };
    }
    const data = trigger.dataset;
    const src = data.lightboxSrc || (trigger as HTMLAnchorElement).href;
    return {
        trigger,
        src,
        title: data.lightboxTitle ?? "",
        description: data.lightboxDescription ?? "",
        details: [data.lightboxMeta, data.lightboxCredit].filter(Boolean).join(" · "),
        download: data.lightboxDownload,
        surface: data.lightboxSurface || surfaceFor(src),
    };
}

/** The pictures to step through from `trigger`, with the index of the one it opens. */
function galleryFor(trigger: HTMLElement): { pictures: Picture[]; index: number } {
    const group = trigger.closest<HTMLElement>("[data-lightbox-group]");
    const scope = group ?? trigger.closest("article, section, main") ?? document.body;
    const seen = new Set<string>();
    const pictures = [...scope.querySelectorAll<HTMLElement>(TRIGGERS)]
        .filter(
            (candidate) =>
                // An explicit group keeps every member, including tiles a collapsed gallery hides.
                group ||
                (!candidate.closest("[data-lightbox-group], .slick-cloned") &&
                    candidate.getClientRects().length > 0),
        )
        .map(pictureFor)
        .filter((picture) => !seen.has(picture.src) && seen.add(picture.src));
    // A carousel's clone or a hidden copy stands for the original with the same picture.
    const opened = pictureFor(trigger);
    const index = pictures.findIndex((picture) => picture.src === opened.src);
    return index >= 0 ? { pictures, index } : { pictures: [opened], index: 0 };
}

function mountImageLightbox(dialog: HTMLDialogElement) {
    const part = <T extends Element>(name: string) =>
        dialog.querySelector<T>(`[data-lightbox-${name}]`)!;
    const stage = part<HTMLElement>("stage");
    const image = part<HTMLImageElement>("image");
    const caption = part<HTMLElement>("caption");
    const title = part<HTMLElement>("title");
    const description = part<HTMLElement>("description");
    const details = part<HTMLElement>("details");
    const count = part<HTMLElement>("count");
    const download = part<HTMLAnchorElement>("download");
    const previous = part<HTMLButtonElement>("previous");
    const next = part<HTMLButtonElement>("next");

    let pictures: Picture[] = [];
    let index = 0;

    function show(at: number) {
        index = (at + pictures.length) % pictures.length;
        const picture = pictures[index];

        // Start from the page's own copy, already loaded, and sharpen once the large one is in.
        const thumbnail =
            picture.trigger instanceof HTMLImageElement
                ? picture.trigger.currentSrc
                : picture.trigger.querySelector("img")?.currentSrc;
        image.alt =
            picture.trigger instanceof HTMLImageElement ? picture.trigger.alt : picture.title;
        image.toggleAttribute("data-surface", false);
        if (picture.surface) {
            image.dataset.surface = picture.surface;
        }
        // A lazy-loaded image not yet in view still shows its placeholder.
        if (thumbnail && !thumbnail.startsWith("data:") && thumbnail !== picture.src) {
            image.src = thumbnail;
            dialog.dataset.loading = "";
            const loader = new Image();
            loader.src = picture.src;
            loader
                .decode()
                .catch(() => undefined)
                .then(() => {
                    if (pictures[index] === picture) {
                        image.src = picture.src;
                        delete dialog.dataset.loading;
                    }
                });
        } else {
            image.src = picture.src;
            delete dialog.dataset.loading;
        }

        title.textContent = picture.title;
        description.textContent = picture.description;
        details.textContent = picture.details;
        download.hidden = !picture.download;
        download.href = picture.download ?? "";
        count.textContent = pictures.length > 1 ? `${index + 1} / ${pictures.length}` : "";
        previous.hidden = next.hidden = pictures.length < 2;
        caption.hidden = ![
            picture.title,
            picture.description,
            picture.details,
            picture.download,
            count.textContent,
        ].some(Boolean);

        // Fetch the neighbours' large pictures ahead, so stepping through feels instant.
        for (const neighbour of [pictures[index - 1], pictures[index + 1]]) {
            if (neighbour) {
                new Image().src = neighbour.src;
            }
        }
    }

    function open(trigger: HTMLElement) {
        ({ pictures, index } = galleryFor(trigger));
        show(index);
        if (!dialog.open) {
            dialog.showModal();
            trackModal("Open", "Image Viewer");
        }
    }

    document.addEventListener("click", (event) => {
        const trigger = (event.target as Element).closest<HTMLElement>(TRIGGERS);
        if (trigger && isPlainClick(event) && !dialog.contains(trigger)) {
            event.preventDefault();
            open(trigger);
        }
    });

    // Images are not keyboard targets of their own; prepareImages() makes them focusable.
    document.addEventListener("keydown", (event) => {
        const target = event.target as Element;
        if (
            (event.key === "Enter" || event.key === " ") &&
            target instanceof HTMLImageElement &&
            target.matches(TRIGGERS)
        ) {
            event.preventDefault();
            open(target);
        }
    });

    previous.addEventListener("click", () => show(index - 1));
    next.addEventListener("click", () => show(index + 1));
    dialog.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") {
            show(index - 1);
        } else if (event.key === "ArrowRight") {
            show(index + 1);
        }
    });

    let touchStartX: number | null = null;
    stage.addEventListener("touchstart", (event) => (touchStartX = event.touches[0].clientX), {
        passive: true,
    });
    stage.addEventListener("touchend", (event) => {
        if (touchStartX === null || pictures.length < 2) {
            return;
        }
        const distance = event.changedTouches[0].clientX - touchStartX;
        touchStartX = null;
        if (Math.abs(distance) > 50) {
            show(index + (distance < 0 ? 1 : -1));
        }
    });

    // The picture fills the stage and letterboxes itself, so a click beside it is a backdrop click.
    closeOnDismiss(dialog, "[data-lightbox-close]", (event) => {
        const target = event.target as Element;
        return (
            target === stage ||
            target === dialog ||
            (target === image && isOutsidePicture(image, event))
        );
    });
    dialog.addEventListener("close", () => trackModal("Close", "Image Viewer"));
}

/**
 * Lets keyboard and screen reader users open `img[data-zoom]` images. A carousel's cloned
 * slides copy these attributes, so their images are taken back out of the tab order.
 */
function prepareImages() {
    for (const image of document.querySelectorAll<HTMLImageElement>("img[data-zoom]")) {
        if (image.closest(".slick-cloned")) {
            image.tabIndex = -1;
        } else if (!image.hasAttribute("tabindex")) {
            image.tabIndex = 0;
            image.setAttribute("role", "button");
            image.setAttribute("aria-haspopup", "dialog");
        }
    }
}

/** Wires up the lightbox and makes the page's zoomable images keyboard targets. */
export function startImageLightbox(dialog: HTMLDialogElement) {
    mountImageLightbox(dialog);
    prepareImages();
    // Carousels clone their slides once the page is ready, and the webshop signals content it
    // renders with Vue through jQuery (Scripts/v1/webshop.js), so prepare again then.
    window.addEventListener("load", prepareImages, { once: true });
    (
        window as unknown as {
            jQuery?: (target: Document) => { on(event: string, handler: () => void): void };
        }
    )
        .jQuery?.(document)
        .on("vue-loaded", prepareImages);
}
